

































































































import { debounce } from 'debounce';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { MobileGetter } from 'frontend/types';
import {
  LandingLearningPath,
  LandingLearningPathsState,
} from 'frontend/store/modules/landing-learning-paths';

interface LearningPathExtraData {
  titleOverride?: string;
  videoBlobUrl?: string;
}

const VIDEO_ASSETS_DIR = '/site_media/version-V87e099514e3/images/landing/learning-paths';

@Component({
  computed: {
    ...mapGetters('pageHeader', ['isMobile']),
    ...mapState('landingLearningPaths', {
      landingLearningPaths: (state: LandingLearningPathsState) => state.items,
    }),
  },
})
export default class LearningPaths extends Vue {
  $refs!: {
    buttonContainer: HTMLElement;
    buttons: HTMLElement;
    contentWrapper: HTMLElement;
  };
  @Prop({ type: Boolean, default: false }) private readonly isBelowFold!: boolean;
  @Prop({ type: String, required: true }) readonly axIdPrefix!: string;

  private isMobile!: MobileGetter;
  private landingLearningPaths!: LandingLearningPath[];

  // In order for any learning path coming from server to actually be rendered, this record should
  // contain the path's slug as a key.
  // If you add a new slug here, make sure to also provide the following things for it:
  // - button icon (/frontend/sprites/landing/path-<slug>.png)
  // - outline color for the button (selector: .learning-paths .buttons li.selected.<slug>})
  // - title override if necessary (e.g. if the original title is too long to fit the button)
  private ALLOWED_LEARNING_PATHS: Record<string, LearningPathExtraData> = {
    'math-foundations': { titleOverride: 'Foundational Math' },
    'software-development-career': { titleOverride: 'Software Development' },
    'college-science': { titleOverride: 'Science' },
    'logic-foundations': { titleOverride: 'Foundational Logic' },
    'data-science-career': { titleOverride: 'Data Science' },
    'high-school-mathematics': { titleOverride: 'High School Math' },
    'engineering-career': { titleOverride: 'Engineering' },
    'probability-statistics-finance': { titleOverride: 'Statistics and Finance' },
  };
  private selectedPathSlug = 'math-foundations';
  private debouncedHandleResize = debounce(this.handleResize, 50);
  private timesScrolledButtons = 0;
  // default values for SSR
  private buttonWidth = 302;
  private buttonContainerWidth = 1176;
  private pageWidth = 1176;

  private get mobile() {
    return this.isMobile(this.$mq);
  }

  private getLearningPathTitle(path: LandingLearningPath): string {
    return this.ALLOWED_LEARNING_PATHS[path.slug].titleOverride || path.title;
  }

  private getLearningPathVideoURL(path: LandingLearningPath): string {
    return (
      this.ALLOWED_LEARNING_PATHS[path.slug].videoBlobUrl || `${VIDEO_ASSETS_DIR}/${path.slug}.mp4`
    );
  }

  private getPathAxId(path: LandingLearningPath): string {
    return `clicked_${this.axIdPrefix}_learning_path_${path.slug.replace(/-/g, '_')}`;
  }

  private isPathSelected(path: LandingLearningPath): boolean {
    return path.slug === this.selectedPathSlug;
  }

  private isPathAllowed(path: LandingLearningPath): boolean {
    return path.slug in this.ALLOWED_LEARNING_PATHS;
  }

  private get selectedPath(): LandingLearningPath {
    return this.landingLearningPaths.filter(lp => lp.slug === this.selectedPathSlug)[0];
  }

  private get canScrollButtons(): boolean {
    return this.buttonContainerWidth > this.pageWidth;
  }

  private get buttonsScrollPosition(): number {
    const scrollPosition = this.buttonWidth * this.timesScrolledButtons - 35;
    const maxScrollPosition = this.buttonContainerWidth - this.pageWidth;

    if (scrollPosition < 0) {
      return 0;
    }
    if (scrollPosition > maxScrollPosition) {
      return maxScrollPosition;
    }
    return scrollPosition;
  }

  private get maxButtonScrolls(): number {
    return Math.ceil((this.buttonContainerWidth - this.pageWidth) / this.buttonWidth);
  }

  private onTransitionEnter(el: Element): void {
    if (this.mobile) {
      this.setCourseListContainersWidth();
    }
    if (el.clientHeight > this.$refs.contentWrapper.clientHeight) {
      // change the container height first to prevent content from overflowing
      this.$refs.contentWrapper.style.height = `${el.clientHeight}px`;
    }
  }

  private resetWrapperHeight(): void {
    this.$refs.contentWrapper.style.height = '';
  }

  /** Set optimal width for course list containers so that they each have exactly 2 rows */
  private setCourseListContainersWidth(): void {
    [...document.querySelectorAll('.course-names')].forEach(list => {
      const items = [...(list as HTMLElement).children];
      const itemWidths = items.map((c: Element) => c.clientWidth);
      const totalWidth = itemWidths.reduce((a, b) => a + b, 0);
      const safetyMargin = 5; // to avoid quirky wrapping
      let width;

      if (items.length < 4) {
        width = totalWidth;
      } else {
        // let's start with all the course names on the 2nd row
        let firstRowWidth = 0;
        let secondRowWidth = totalWidth;
        let minDelta = Number.MAX_VALUE;
        let delta;

        do {
          // would it look better if we move one item from the 2nd row to the 1st one?
          delta = Math.abs(firstRowWidth + itemWidths[0] - (secondRowWidth - itemWidths[0]));
          if (delta < minDelta) {
            minDelta = delta;
            firstRowWidth += itemWidths[0];
            secondRowWidth -= itemWidths[0];
            itemWidths.shift();
          }
        } while (delta <= minDelta);

        width = Math.max(firstRowWidth, secondRowWidth);
      }

      (list as HTMLElement).style.width = `${width + safetyMargin}px`;
    });
  }

  private handleResize(): void {
    if (this.$refs.buttons) {
      const button = this.$refs.buttons.children[0];
      const buttonStyle = window.getComputedStyle(button);
      this.buttonWidth = parseInt(buttonStyle.width, 10) + parseInt(buttonStyle.marginRight, 10);
      this.buttonContainerWidth = this.$refs.buttons.clientWidth;
    }
    if (this.$refs.buttonContainer) {
      this.pageWidth = this.$refs.buttonContainer.clientWidth;
    }
  }

  private preloadVideos(): void {
    this.landingLearningPaths.forEach(path => {
      const req = new XMLHttpRequest();
      req.open('GET', this.getLearningPathVideoURL(path), true);
      req.responseType = 'blob';
      req.onload = () => {
        if (req.status === 200) {
          // Browsers are too smart nowadays to preload the entire video file.
          // Creating blob URL does the trick.
          this.ALLOWED_LEARNING_PATHS[path.slug].videoBlobUrl = URL.createObjectURL(req.response);
        }
      };
      req.send();
    });
    window.removeEventListener('scroll', this.preloadVideos);
  }

  private mounted(): void {
    this.handleResize();
    window.addEventListener('resize', this.debouncedHandleResize);
    window.addEventListener('scroll', this.preloadVideos);
    if (this.mobile) {
      this.setCourseListContainersWidth();
    }
  }

  private destroyed(): void {
    window.removeEventListener('resize', this.debouncedHandleResize);
    window.removeEventListener('scroll', this.preloadVideos);
  }
}
